import { lazy, Suspense, useEffect } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { policyQueries } from '@/features/air/policy-management/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { markOnboardingTaskAsComplete } from '@/features/onboarding/actions'
import { useOnboardingTask } from '@/features/onboarding/store'

import { getCurrentTenant } from '@/shared/auth/store'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'

import { ErrorBoundary } from '@/components/common/error-boundary'

const PolicyManagementTable = lazy(
	() => import('@/features/air/policy-management/components/page'),
)

const searchSchema = z.object({
	startTime: z.string().optional(),
	// Every key a filter group can write. A key missing here is **silently
	// stripped** from the URL by the validator, so the dropdown appears to do
	// nothing — add the group and its key together.
	filters: z
		.object({
			status: z.string().optional(),
			severity: z.string().optional(),
			source: z.string().optional(),
			/** Where the rule came from — `PolicyType`. */
			type: z.string().optional(),
			/** What the rule inspects — `PolicyCategory`. */
			category: z.string().optional(),
		})
		.optional(),
	q: z.string().optional(),
	policy: z.string().optional(),
})

export const Route = createFileRoute(
	'/_dashboard/air/_layout/policy-management',
)({
	validateSearch: zodValidator(searchSchema),
	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		if (tenant) {
			queryClient.prefetchQuery(policyQueries.policies_list(tenant))
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Policy Management | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RouteSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const policiesTask = useOnboardingTask('reviewPolicies')

	useEffect(() => {
		if (!policiesTask?.completed && !policiesTask?.skipped) {
			markOnboardingTaskAsComplete('reviewPolicies')
		}
	}, [policiesTask?.completed, policiesTask?.skipped])

	return (
		<ErrorBoundary>
			<Suspense
				fallback={
					<TableFullSkeleton
						className="w-full"
						rows={10}
						withFilters
						withLastUpdated
					/>
				}
			>
				<PolicyManagementTable />
			</Suspense>
		</ErrorBoundary>
	)
}

function RouteSkeleton() {
	return (
		<TableFullSkeleton
			className="w-full"
			rows={10}
			withFilters
			withLastUpdated
		/>
	)
}
